/**
 * Machine page › Condition register.
 *
 * The demo's `compRow(c)` (line 2174) repeated over `machineComponents(id)`:
 * one row per component, worst band first, carrying the score out of 100, a
 * band bar, hours since change against the change-out interval, and the
 * planner's note where there is one.
 *
 * Clicking a row selects that component, which is what drives the illustration
 * callout and the work-history filter — the demo's own cross-highlight.
 *
 * UI layer; the arithmetic is `componentRow()` in the engine.
 *
 * @see src/versions/v1/modules/fleet/MachineScene.transform.ts
 * @see docs/logic/fleet/machine-page.md
 */

import { cn } from '@/shared/cn';
import { groupNum } from '../../shared/oem-engine';
import type { KeyedComponentRow } from './MachineScene.transform';

export interface MachineConditionTabProps {
  rows: readonly KeyedComponentRow[];
  selected: string | null;
  onSelect: (component: string | null) => void;
}

export function MachineConditionTab({ rows, selected, onSelect }: MachineConditionTabProps) {
  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const active = row.component === selected;
        return (
          <li key={row.key}>
            <button
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(active ? null : row.component)}
              className={cn(
                'w-full rounded-xl border p-3 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)]',
                active
                  ? 'border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)]'
                  : 'border-[var(--glass-border)] bg-[var(--glass-surface)] hover:bg-[var(--glass-surface-strong)]',
              )}
            >
              <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-medium text-zinc-900 dark:text-zinc-100">
                  {row.component}
                </span>
                <span className="mono text-sm" title={row.band.label}>
                  {row.score}
                  <span className="text-muted">/100</span>
                </span>
                <span className={`band-pill band-${row.band.key}`}>{row.band.label}</span>
              </span>

              <span
                className="mt-2 block h-1.5 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800"
                aria-hidden="true"
              >
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${row.score}%`, background: `var(--band-${row.band.key})` }}
                />
              </span>

              <span className="mt-1.5 block text-[11px] text-muted">
                {groupNum(row.hoursSinceChange)} h since change · interval{' '}
                {groupNum(row.interval)} h ·{' '}
                {row.remaining >= 0
                  ? `${groupNum(row.remaining)} h remaining`
                  : `overdue by ${groupNum(-row.remaining)} h`}
              </span>

              {row.note ? (
                <span className="mt-1 block text-[11px] italic text-muted">{row.note}</span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
